// @ts-nocheck

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { BASE_URL } from "../Constants/BaseUrl";
import { RootState } from "../store"; // Adjust the import if RootState is declared elsewhere
import { toast } from "react-toastify";

// Sport interface
export interface Sport {
  id: number;
  sport_name: string;
  created_at: string;
}

// Athlete interface
export interface Athlete {
  sport_id: number;
  position: string;
  height_cm: number;
  weight_kg: number;
  dominant_hand: string;
  years_of_experience: number;
  achievements: string;
}

// Sports state interface
interface SportsState {
  sportsData: Sport[]; // List of sports
  athleteData: Athlete | null; // Single athlete data
  loading: boolean;
  error: string | null;
}

// Initial state for the sports slice
const initialState: SportsState = {
  sportsData: [],
  athleteData: null,
  loading: false,
  error: null,
};

// Async thunk to fetch athlete info
export const fetchAthleteInfo = createAsyncThunk<
  Athlete, // ✅ Returned data type
  number, // ✅ Argument (athlete ID)
  { state: RootState }
>("athlete/fetchAthleteInfo", async (athleteId, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const token = state.user.token;

    if (!token) throw new Error("User not authenticated");

    const response = await fetch(`${BASE_URL}/user/get-profile`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const result = await response.json();

    console.log(result, "TestingApi");

    if (!response.ok) {
      throw new Error(result.message || "Failed to fetch athlete info");
    }

    return result.data as Athlete;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

// 🔄 Async thunk to fetch sports data
export const fetchSports = createAsyncThunk(
  "sports/fetchSports/all",
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.user.token;

      if (!token) throw new Error("User not authenticated");

      console.log("Okay");
      const response = await fetch(`${BASE_URL}sports/all`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      console.log(response);

      if (!response.ok) {
        throw new Error("Failed to fetch sports");
      }

      const result = await response.json();

      console.log(result, "SportsData");

      return result.data as Sport[];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// 🆕 Async thunk to update athlete info
export const updateAthleteInfo = createAsyncThunk<
  Athlete, // Return type is the updated athlete data
  Athlete, // Payload is the new athlete data to update
  { state: RootState }
>("athlete/updateAthleteInfo", async (athleteData, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const token = state.user.token;

    if (!token) throw new Error("User not authenticated");

    const response = await fetch(`${BASE_URL}athlete/update-athletic-info`, {
      method: "POST", // or PUT if your backend expects it
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(athleteData),
    });

    const result = await response.json();

    console.log(result);

    if (!response.ok) {
      throw new Error(result.message || "Update failed");
    }

    toast("Athlete info updated successfully!");
    // ✅ Show success flash message

    // Return the updated athlete data to be stored in the Redux state
    return result.data; // Assuming the response has `data` that includes the updated athlete info
  } catch (error: any) {
    // ❌ Show error flash message
    toast("Something went wrong");

    return thunkAPI.rejectWithValue(error.message);
  }
});

// The sports slice itself
export const sportsSlice = createSlice({
  name: "sports",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAthleteInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchAthleteInfo.fulfilled,
        (state, action: PayloadAction<Athlete>) => {
          state.loading = false;
          state.athleteData = action.payload;
        }
      )
      .addCase(fetchAthleteInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Handling fetch sports actions
      .addCase(fetchSports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSports.fulfilled,
        (state, action: PayloadAction<Sport[]>) => {
          state.loading = false;
          state.sportsData = action.payload;
        }
      )
      .addCase(fetchSports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Handling athlete info update actions
      .addCase(updateAthleteInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        updateAthleteInfo.fulfilled,
        (state, action: PayloadAction<Athlete>) => {
          state.loading = false;
          state.athleteData = action.payload; // Store the updated athlete data in the state
        }
      )
      .addCase(updateAthleteInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default sportsSlice.reducer;
